import { Application, Container, Renderer } from 'pixi.js';

declare global {
  var __PIXI_APP__: Application | undefined;
  var __PIXI_STAGE__: Container | undefined;
  var __PIXI_RENDERER__: Renderer | undefined;
}

/**
 * FixedPixiCanvas — A Pixi renderer at a fixed 1440x1080 resolution.
 * Never resizes on window resize. Used by games with `layout: 'scaled-fixed'`.
 */
export class FixedPixiCanvas {
  app!: Application;
  root!: Container;

  static readonly VIRTUAL_WIDTH = 1440;
  static readonly VIRTUAL_HEIGHT = 1080;

  async mount(el: HTMLElement) {
    this.app = new Application();
    await this.app.init({
      width: FixedPixiCanvas.VIRTUAL_WIDTH,
      height: FixedPixiCanvas.VIRTUAL_HEIGHT,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      antialias: true,
      background: '#0e0e0e',
    });

    // Expose PIXI application globally for browser extensions and dev tools
    globalThis.__PIXI_APP__ = this.app;
    globalThis.__PIXI_STAGE__ = this.app.stage;
    globalThis.__PIXI_RENDERER__ = this.app.renderer;

    console.log('[FixedPixiCanvas] Initialized at 1440x1080 (fixed)');
    console.log('[FixedPixiCanvas] Canvas size:', {
      width: this.app.screen.width,
      height: this.app.screen.height,
      canvasWidth: this.app.canvas.width,
      canvasHeight: this.app.canvas.height,
    });

    this.root = new Container();
    this.app.stage.addChild(this.root);

    // Style canvas to fill the mount point exactly
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';

    canvas.addEventListener('webglcontextlost', () => {
      console.error('WEBGL LOST');
    });
    canvas.addEventListener('webglcontextrestored', () => {
      console.error('WEBGL RESTORED');
    });

    el.appendChild(canvas);
  }

  unmount() {
    if (globalThis.__PIXI_APP__ === this.app) {
      delete globalThis.__PIXI_APP__;
      delete globalThis.__PIXI_STAGE__;
      delete globalThis.__PIXI_RENDERER__;
    }
    this.app?.destroy(true, { children: true, texture: true });
  }
}
